import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import CrossIcon from "@/assets/svg/crossIcon.svg";
import CrossDarkIcon from "@/assets/svg/crossDarkIcon.svg";
import UpdatePopWarningDark from "@/assets/svg/UpdatePopWarningDark.svg";

export interface ModalButtonConfig {
  label: string;
  onClick: () => void;
  variant?: "contained1" | "outline";
  icon?: string | React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  subText?: string;
  showWarning?: boolean;
  warningText?: string;
  warningIcon?: string | React.ReactNode;
  modalWidth?: string;
  buttons: ModalButtonConfig[];
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  subText,
  showWarning,
  warningText,
  warningIcon,
  modalWidth = "583px",
  buttons,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden sm:max-w-none !animate-none data-[state=open]:fade-in data-[state=open]:animate-in data-[state=closed]:fade-out data-[state=closed]:animate-out"
        style={{ width: modalWidth }}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-6 h-[48px] min-h-[48px]">
          <DialogTitle className="contents">
            <Typography variant="semibold-16" className="text-grey-800 dark:text-grey-100 py-2">
              {title}
            </Typography>
          </DialogTitle>
          <DialogClose asChild>
            <div
              role='button'
              aria-label='Close'
              onClick={() => onClose()}
              className='
              h-[30px] w-[30px]
              bg-grey-200
              hover:bg-grey-300
              dark:bg-[#32414A]
              dark:hover:bg-gray-600
              text-muted-foreground
              hover:none
              flex items-center justify-center
              rounded-md
              cursor-pointer
              transition-all
              shrink-0
               '>
              <img
                src={CrossIcon}
                alt='crossicon'
                className=' block dark:hidden'
              />
              <img
                src={CrossDarkIcon}
                alt='crossicon'
                className='hidden dark:block'
              />
            </div>
          </DialogClose>
        </div>
        <div className="h-px bg-grey-200 dark:bg-grey-700 w-full" />

        {/* Body */}
        <div className={`flex flex-col ${showWarning ? "gap-5" : "gap-6"} px-6 pt-4 pb-2`}>
          <div className="flex flex-col gap-2">
            <Typography variant="semibold-16" className="text-grey-800 dark:text-grey-200">
              {description}
            </Typography>
            {subText && (
              <Typography variant="regular-14" className="text-grey-500 dark:text-grey-400 whitespace-pre-line">
                {subText}
              </Typography>
            )}
          </div>

          {showWarning && warningText && (
            <div
              className="flex items-center gap-3 bg-warning-100 dark:bg-blue-700 dark:border dark:border-warning-500 rounded-md flex-shrink-0"
              style={{ marginLeft: "-6px", marginRight: "-6px", height: "50px", paddingLeft: "12px", paddingRight: "12px" }}
            >
              {warningIcon && (
                <span
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: "26px", height: "26px" }}
                >
                  {typeof warningIcon === "string" ? (
                    <>
                      <img src={warningIcon} alt="warning" style={{ width: "26px", height: "26px" }} className="block dark:hidden" />
                      <img src={UpdatePopWarningDark} alt="warning" style={{ width: "26px", height: "26px" }} className="hidden dark:block" />
                    </>
                  ) : (
                    warningIcon
                  )}
                </span>
              )}
              <Typography variant="medium-14" className="text-banner-variant dark:text-grey-100">
                {warningText}
              </Typography>
            </div>
          )}
        </div>

        {/* Footer – dynamic buttons */}
        <DialogFooter className="px-6 pb-6 pt-4 flex flex-row items-center justify-end gap-3 sm:space-x-0">
          {buttons.map((btn, index) => (
            <Button
              key={index}
              variant={btn.variant ?? "contained1"}
              onClick={btn.onClick}
              className={`h-[40px] ${btn.className ?? ""}`}
              loading={btn.isLoading}
              startIcon={
                btn.icon ? (
                  typeof btn.icon === "string" ? (
                    <img src={btn.icon} alt="button icon" className="w-[18px] h-[18px]" />
                  ) : (
                    btn.icon
                  )
                ) : undefined
              }
            >
              {btn.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationModal;
